import * as React from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { MinusIcon } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Input OTP Component
 *
 * An accessible one-time password component with copy-paste functionality.
 * Built on the input-otp library with shadcn/ui design standards.
 *
 * @example
 * ```tsx
 * <InputOTP maxLength={6}>
 *   <InputOTPGroup>
 *     <InputOTPSlot index={0} />
 *     <InputOTPSlot index={1} />
 *     <InputOTPSlot index={2} />
 *   </InputOTPGroup>
 *   <InputOTPSeparator />
 *   <InputOTPGroup>
 *     <InputOTPSlot index={3} />
 *     <InputOTPSlot index={4} />
 *     <InputOTPSlot index={5} />
 *   </InputOTPGroup>
 * </InputOTP>
 * ```
 */
const InputOTP = React.forwardRef<
    React.ElementRef<typeof OTPInput>,
    React.ComponentProps<typeof OTPInput> & {
        containerClassName?: string;
    }
>(({ className, containerClassName, ...props }, ref) => (
    <OTPInput
        ref={ref}
        data-slot="input-otp"
        containerClassName={cn(
            "setu:flex setu:items-center setu:gap-2 setu:has-disabled:opacity-50",
            containerClassName
        )}
        className={cn("setu:disabled:cursor-not-allowed", className)}
        {...props}
    />
));
InputOTP.displayName = "InputOTP";

/**
 * Input OTP Group Component
 *
 * Groups OTP input slots together.
 *
 * @example
 * ```tsx
 * <InputOTPGroup>
 *   <InputOTPSlot index={0} />
 *   <InputOTPSlot index={1} />
 * </InputOTPGroup>
 * ```
 */
const InputOTPGroup = React.forwardRef<React.ElementRef<"div">, React.ComponentProps<"div">>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            data-slot="input-otp-group"
            className={cn("setu:flex setu:items-center", className)}
            {...props}
        />
    )
);
InputOTPGroup.displayName = "InputOTPGroup";

/**
 * Input OTP Slot Component
 *
 * Individual slot for OTP input.
 *
 * @example
 * ```tsx
 * <InputOTPSlot index={0} />
 * ```
 */
const InputOTPSlot = React.forwardRef<
    React.ElementRef<"div">,
    React.ComponentProps<"div"> & {
        index: number;
    }
>(({ index, className, ...props }, ref) => {
    const inputOTPContext = React.useContext(OTPInputContext);
    const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};

    return (
        <div
            ref={ref}
            data-slot="input-otp-slot"
            data-active={isActive}
            className={cn(
                "setu:data-[active=true]:!border-ring setu:data-[active=true]:ring-ring/50 setu:data-[active=true]:aria-invalid:ring-destructive/20 dark:setu:data-[active=true]:aria-invalid:ring-destructive/40 setu:aria-invalid:!border-destructive setu:data-[active=true]:aria-invalid:!border-destructive setu:bg-background setu:!border-input setu:relative setu:flex setu:h-9 setu:w-9 setu:items-center setu:justify-center setu:!border-y setu:!border-r setu:text-sm setu:shadow-xs setu:transition-all setu:outline-none setu:first:rounded-l-md setu:first:!border-l setu:last:rounded-r-md setu:data-[active=true]:z-10 setu:data-[active=true]:ring-[3px]",
                className
            )}
            {...props}
        >
            {char}
            {hasFakeCaret && (
                <div className="setu:pointer-events-none setu:absolute setu:inset-0 setu:flex setu:items-center setu:justify-center">
                    <div className="animate-caret-blink setu:bg-foreground setu:h-4 setu:w-px setu:duration-1000" />
                </div>
            )}
        </div>
    );
});
InputOTPSlot.displayName = "InputOTPSlot";

/**
 * Input OTP Separator Component
 *
 * Visual separator between OTP groups.
 *
 * @example
 * ```tsx
 * <InputOTPSeparator />
 * ```
 */
const InputOTPSeparator = React.forwardRef<React.ElementRef<"div">, React.ComponentProps<"div">>(
    ({ ...props }, ref) => (
        <div ref={ref} data-slot="input-otp-separator" role="separator" {...props}>
            <MinusIcon />
        </div>
    )
);
InputOTPSeparator.displayName = "InputOTPSeparator";

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
